import { createSignal, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { crypto } from "@boveda/shared";
import { ShieldCheck } from "lucide-solid";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Logo } from "@/components/ui/logo";
import { useI18n } from "@/providers/i18n-provider";
import { useVault } from "../vault-context";

const MIN_PASSWORD_LENGTH = 8;

/** Primer arranque: crea la cuenta zero-knowledge con la contraseña maestra. */
export function VaultSetup() {
  const { t } = useI18n();
  const vault = useVault();
  const [password, setPassword] = createSignal("");
  const [confirm, setConfirm] = createSignal("");
  const [error, setError] = createSignal("");
  const [busy, setBusy] = createSignal(false);

  const strength = () => crypto.estimateStrength(password());

  async function handleSubmit(e: Event): Promise<void> {
    e.preventDefault();
    setError("");
    if (password().length < MIN_PASSWORD_LENGTH) {
      setError(t(msg`Use at least 8 characters.`));
      return;
    }
    if (password() !== confirm()) {
      setError(t(msg`Passwords do not match.`));
      return;
    }
    setBusy(true);
    try {
      await vault.setup(password());
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center gap-6 px-4">
      <Logo size="lg" />
      <div class="flex items-center gap-2 text-center text-muted-foreground">
        <ShieldCheck class="h-5 w-5 text-primary" />
        <p class="text-sm">{t(msg`Create your master password. It never leaves this device.`)}</p>
      </div>

      <form class="flex w-full flex-col gap-4" onSubmit={(e) => void handleSubmit(e)}>
        <div class="flex flex-col gap-1.5">
          <Label for="master-password">{t(msg`Master password`)}</Label>
          <TextField
            id="master-password"
            type="password"
            autocomplete="new-password"
            value={password()}
            onInput={(e) => setPassword(e.currentTarget.value)}
          />
          <Show when={password().length > 0}>
            <div class="h-1 w-full overflow-hidden rounded bg-muted">
              <div
                class="h-full rounded transition-all"
                classList={{
                  "bg-destructive": strength().score < 2,
                  "bg-warning": strength().score === 2,
                  "bg-success": strength().score > 2,
                }}
                style={{ width: `${((strength().score + 1) / 5) * 100}%` }}
              />
            </div>
          </Show>
        </div>

        <div class="flex flex-col gap-1.5">
          <Label for="master-password-confirm">{t(msg`Confirm master password`)}</Label>
          <TextField
            id="master-password-confirm"
            type="password"
            autocomplete="new-password"
            value={confirm()}
            onInput={(e) => setConfirm(e.currentTarget.value)}
          />
        </div>

        <Show when={error()}>
          <p class="text-sm text-destructive" role="alert">
            {error()}
          </p>
        </Show>

        <Button type="submit" loading={busy()} data-testid="vault-setup-submit">
          {t(msg`Create vault`)}
        </Button>
        <p class="text-center text-xs text-muted-foreground">
          {t(msg`If you forget it, your data cannot be recovered.`)}
        </p>
      </form>
    </div>
  );
}
